/**
 * Panel vượt hạn mức gói dịch vụ ở footer. Dữ liệu là `session.service_quota` (backend dựng
 * sẵn câu `notice` theo vai trò).
 *
 * - Admin: warning, hiện ngay khi đợt mở, bấm để xử lý.
 * - Nhân viên: info, hiện khi đang tạm khoá, hoặc từ `staff_visible_at` khi mới vượt
 *   (admin được báo trước 4 giờ).
 * - Thứ tự trên footer: tài khoản khoá › đang tạm khoá ghi › warningInfos › đang vượt.
 */

export function resolveServiceQuotaNotice(session, quota, nowUnix) {
    if (!quota || !quota.notice || (quota.status !== 'Open' && quota.status !== 'Locked')) return null;
    if (session && session.account_status === 'Inactive') return null;

    const hasWarningInfos = Boolean(session) && Array.isArray(session.warningInfos) && session.warningInfos.length > 0;
    if (quota.status === 'Open' && hasWarningInfos) return null;

    if (quota.is_admin) return { type: 'warning', message: quota.notice, actionable: true };

    if (quota.status === 'Open') {
        const visibleAt = Number(quota.staff_visible_at) || 0;
        if (!visibleAt || nowUnix < visibleAt) return null;
    }
    return { type: 'info', message: quota.notice, actionable: false };
}

/** Số giây tới lúc nhân viên bắt đầu thấy panel, để hẹn giờ hiện; null nếu không cần. */
export function secondsUntilStaffNotice(quota, nowUnix) {
    if (!quota || quota.is_admin || quota.status !== 'Open') return null;
    const visibleAt = Number(quota.staff_visible_at) || 0;
    return visibleAt > nowUnix ? visibleAt - nowUnix : null;
}

/** Sự kiện AntAuth.verifyResponse phát khi API trả lỗi hạn mức (133 chạm trần, 135 đang tạm khoá ghi). */
export const SERVICE_QUOTA_ERROR_EVENT = 'ant-service-quota-error';

export const OPEN_BILLING_TEXT = {
    vi: 'Xem gói dịch vụ',
    en: 'View service plans',
    fr: 'Voir les offres',
    es: 'Ver planes de servicio',
    id: 'Lihat paket layanan',
    ja: 'サービスプランを見る',
    zh: '查看服务套餐',
    ko: '서비스 요금제 보기',
    hi: 'सेवा योजनाएँ देखें',
    ar: 'عرض باقات الخدمة'
};

export const QUOTA_ERROR_TOAST_TEXT = {
    vi: 'Nâng gói, mua thêm hạn mức hoặc vô hiệu bớt để tiếp tục.',
    en: 'Upgrade the plan, buy more capacity, or deactivate some to continue.',
    fr: 'Changez d’offre, achetez de la capacité ou désactivez-en pour continuer.',
    es: 'Mejore el plan, compre más capacidad o desactive algunos para continuar.',
    id: 'Tingkatkan paket, beli kapasitas tambahan, atau nonaktifkan sebagian untuk melanjutkan.',
    ja: '続けるには、プランのアップグレード、上限の追加購入、または一部の無効化を行ってください。',
    zh: '请升级套餐、购买更多额度或停用部分项目后继续。',
    ko: '계속하려면 요금제를 업그레이드하거나 한도를 추가 구매하거나 일부를 비활성화하세요.',
    hi: 'जारी रखने के लिए योजना अपग्रेड करें, अतिरिक्त सीमा खरीदें या कुछ निष्क्रिय करें।',
    ar: 'قم بترقية الباقة أو اشترِ سعة إضافية أو عطّل بعضها للمتابعة.'
};

// POS không có màn gói dịch vụ: hướng dẫn admin xử lý trên web hoặc ứng dụng ANTE.
export const POS_RESOLVE_GUIDE_TEXT = {
    vi: 'Mở ANTE trên web (Hệ thống › Dịch vụ) hoặc ứng dụng ANTE (Gói dịch vụ) để nâng gói, mua thêm hạn mức, hoặc vô hiệu những mặt hàng, cửa hàng, kho, người dùng không còn dùng.',
    en: 'Open ANTE on the web (System Config › Services) or the ANTE app (Service plans) to upgrade the plan, buy more capacity, or deactivate products, stores, warehouses and users you no longer use.',
    fr: 'Ouvrez ANTE sur le web (Config système › Services) ou l’application ANTE (Offres) pour changer d’offre, acheter de la capacité ou désactiver les articles, magasins, entrepôts et utilisateurs inutilisés.',
    es: 'Abra ANTE en la web (Config sistema › Services) o la app ANTE (Planes) para mejorar el plan, comprar más capacidad o desactivar productos, tiendas, almacenes y usuarios que ya no use.',
    id: 'Buka ANTE di web (Konfigurasi sistem › Services) atau aplikasi ANTE (Paket layanan) untuk meningkatkan paket, membeli kapasitas tambahan, atau menonaktifkan produk, toko, gudang, dan pengguna yang tidak dipakai.',
    ja: 'Web版ANTE（システム設定 › Services）またはANTEアプリ（サービスプラン）で、プランのアップグレード、上限の追加購入、または使っていない商品・店舗・倉庫・ユーザーの無効化を行ってください。',
    zh: '请在网页版 ANTE（系统配置 › Services）或 ANTE 应用（服务套餐）中升级套餐、购买更多额度，或停用不再使用的商品、门店、仓库和用户。',
    ko: '웹 ANTE(시스템 설정 › Services) 또는 ANTE 앱(서비스 요금제)에서 요금제 업그레이드, 한도 추가 구매, 또는 사용하지 않는 품목·매장·창고·사용자 비활성화를 진행하세요.',
    hi: 'वेब पर ANTE (सिस्टम कॉन्फ़िग › Services) या ANTE ऐप (सेवा योजनाएँ) खोलें और योजना अपग्रेड करें, अतिरिक्त सीमा खरीदें, या उपयोग में न आने वाले उत्पाद, स्टोर, गोदाम और उपयोगकर्ता निष्क्रिय करें।',
    ar: 'افتح ANTE على الويب (إعدادات النظام › Services) أو تطبيق ANTE (باقات الخدمة) لترقية الباقة أو شراء سعة إضافية أو تعطيل المنتجات والمتاجر والمستودعات والمستخدمين غير المستخدمين.'
};
